import React, { useState, useEffect, useCallback } from "react";
import { Search, LayoutGrid, Calendar } from "lucide-react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import InfiniteScroll from "react-infinite-scroll-component";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { fetchEventSearchResultsAction } from "../../slices/eventDirectorySlice";
import {
  FetchEventSearchResultsRequest,
  SearchResultEvent,
  EventSearchFacet,
} from "../../api/fetchEventSearchResults/types";
import { EventDirectoryCard } from "../EventDirectoryCard/EventDirectoryCard";
import { EventDirectoryLoadingSkeleton } from "../EventDirectoryLoadingSkeleton/EventDirectoryLoadingSkeleton";
import { EventDirectoryCardsSkeleton } from "../EventDirectoryCardsSkeleton/EventDirectoryCardsSkeleton";
import EventDirectoryFilterBar from "../EventDirectoryFilterBar/EventDirectoryFilterBar";
import EventDirectoryCalendar from "../EventDirectoryCalendar/EventDirectoryCalendar";
import CurrentFilterChips from "../CurrentFilterChips/CurrentFilterChips";

type ViewMode = "grid" | "calendar";

export default function EventDirectory() {
  const dispatch = useAppDispatch();

  const {
    searchData: events,
    searchTotalCount: totalCount,
    fetchSearchLoading,
    fetchSearchError,
    availableFacets,
  } = useAppSelector((state: RootState) => state.eventDirectory);

  const facets = availableFacets || {
    eventTypes: [],
    categories: [],
    trades: [],
    businessRoles: [],
  };

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [page, setPage] = useState(1);
  const hasMore = events.length < totalCount;
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 500);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [showOnlyTotalAccess, setShowOnlyTotalAccess] = useState(false);
  const [selectedEventTypes, setSelectedEventTypes] = useState<
    EventSearchFacet[]
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<
    EventSearchFacet[]
  >([]);
  const [selectedTrades, setSelectedTrades] = useState<EventSearchFacet[]>([]);
  const [selectedBusinessRoles, setSelectedBusinessRoles] = useState<
    EventSearchFacet[]
  >([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (!isInitialLoading) {
      resetAndFetch();
    }
  }, [
    debouncedSearch,
    showOnlyFavorites,
    showOnlyTotalAccess,
    selectedEventTypes,
    selectedCategories,
    selectedTrades,
    selectedBusinessRoles,
    viewMode,
  ]);

  async function loadInitialData() {
    await resetAndFetch();
    setIsInitialLoading(false);
  }

  async function resetAndFetch() {
    setPage(1);
    await dispatchFetchPage(1);
  }

  async function dispatchFetchPage(pageNumber: number) {
    const params: FetchEventSearchResultsRequest = {
      page: pageNumber,
      pageSize: viewMode === "calendar" ? 100 : 8,
    };

    if (debouncedSearch.trim()) {
      params.searchTerm = debouncedSearch.trim();
    }
    if (showOnlyFavorites) {
      params.showFavorites = true;
    }
    if (showOnlyTotalAccess) {
      params.showOnlyTotalAccess = true;
    }
    if (selectedEventTypes.length > 0) {
      params.eventTypeNames = selectedEventTypes.map((t) => t.name);
    }
    if (selectedCategories.length > 0) {
      params.categoryNames = selectedCategories.map((c) => c.name);
    }
    if (selectedTrades.length > 0) {
      params.tradeNames = selectedTrades.map((t) => t.name);
    }
    if (selectedBusinessRoles.length > 0) {
      params.businessRoleNames = selectedBusinessRoles.map((r) => r.name);
    }

    await dispatch(fetchEventSearchResultsAction(params));
  }

  const fetchMore = async () => {
    if (isPaginationLoading) return;

    setIsPaginationLoading(true);
    const nextPage = page + 1;
    setPage(nextPage);
    await dispatchFetchPage(nextPage);
    setIsPaginationLoading(false);
  };

  const toggleFacet = useCallback(
    (
      facet: EventSearchFacet,
      selected: EventSearchFacet[],
      setter: React.Dispatch<React.SetStateAction<EventSearchFacet[]>>,
    ) => {
      const exists = selected.find((s) => s.name === facet.name);
      if (exists) {
        setter(selected.filter((s) => s.name !== facet.name));
      } else {
        setter([...selected, facet]);
      }
    },
    [],
  );

  function clearAllFilters() {
    setSearchInput("");
    setShowOnlyFavorites(false);
    setShowOnlyTotalAccess(false);
    setSelectedEventTypes([]);
    setSelectedCategories([]);
    setSelectedTrades([]);
    setSelectedBusinessRoles([]);
  }

  if (isInitialLoading) {
    return <EventDirectoryLoadingSkeleton count={8} />;
  }

  return (
    <MainPageWrapper
      error={fetchSearchError || undefined}
      subtitle="Explore and register for events near you or online"
      title="Event Directory"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1">
          <input
            className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 pl-10 text-sm dark:text-white"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search events..."
            type="text"
            value={searchInput}
          />
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
        </div>
        <div className="flex items-center rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-0.5">
          <button
            className={`p-1.5 rounded-md transition-colors ${
              viewMode === "grid"
                ? "bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white"
                : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            }`}
            onClick={() => setViewMode("grid")}
            title="Grid view"
            type="button"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            className={`p-1.5 rounded-md transition-colors ${
              viewMode === "calendar"
                ? "bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white"
                : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            }`}
            onClick={() => setViewMode("calendar")}
            title="Calendar view"
            type="button"
          >
            <Calendar className="h-4 w-4" />
          </button>
        </div>
      </div>

      <EventDirectoryFilterBar
        facets={facets}
        onToggleFavorites={setShowOnlyFavorites}
        onToggleTotalAccess={setShowOnlyTotalAccess}
        selectedBusinessRoles={selectedBusinessRoles}
        selectedCategories={selectedCategories}
        selectedEventTypes={selectedEventTypes}
        selectedTrades={selectedTrades}
        showOnlyFavorites={showOnlyFavorites}
        showOnlyTotalAccess={showOnlyTotalAccess}
        toggleBusinessRole={(facet) =>
          toggleFacet(facet, selectedBusinessRoles, setSelectedBusinessRoles)
        }
        toggleCategory={(facet) =>
          toggleFacet(facet, selectedCategories, setSelectedCategories)
        }
        toggleEventType={(facet) =>
          toggleFacet(facet, selectedEventTypes, setSelectedEventTypes)
        }
        toggleTrade={(facet) =>
          toggleFacet(facet, selectedTrades, setSelectedTrades)
        }
      />

      <CurrentFilterChips
        onClearAll={clearAllFilters}
        onRemoveBusinessRole={(name) =>
          setSelectedBusinessRoles((prev) =>
            prev.filter((r) => r.name !== name),
          )
        }
        onRemoveCategory={(name) =>
          setSelectedCategories((prev) => prev.filter((c) => c.name !== name))
        }
        onRemoveEventType={(name) =>
          setSelectedEventTypes((prev) => prev.filter((t) => t.name !== name))
        }
        onRemoveFavorites={() => setShowOnlyFavorites(false)}
        onRemoveSearchInput={() => setSearchInput("")}
        onRemoveTrade={(name) =>
          setSelectedTrades((prev) => prev.filter((t) => t.name !== name))
        }
        searchInput={searchInput}
        selectedBusinessRoles={selectedBusinessRoles}
        selectedCategories={selectedCategories}
        selectedEventTypes={selectedEventTypes}
        selectedTrades={selectedTrades}
        showOnlyFavorites={showOnlyFavorites}
      />

      {viewMode === "calendar" ? (
        fetchSearchLoading ? (
          <EventDirectoryCardsSkeleton count={4} />
        ) : (
          <EventDirectoryCalendar events={events} />
        )
      ) : fetchSearchLoading && page === 1 ? (
        <EventDirectoryCardsSkeleton count={8} />
      ) : (
        <InfiniteScroll
          dataLength={events.length}
          endMessage={
            events.length > 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500 dark:text-gray-400">
                  No more events to load
                </p>
              </div>
            ) : null
          }
          hasMore={hasMore}
          loader={
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400">
                Loading more...
              </p>
            </div>
          }
          next={fetchMore}
          scrollableTarget="main-scroll-area"
          style={{ overflow: "visible" }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {events.length > 0 ? (
              events.map((evt: SearchResultEvent) => (
                <div className="fade-in" key={evt.uuid}>
                  <EventDirectoryCard event={evt} />
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  No events found matching your criteria.
                </p>
                <button
                  className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
                  onClick={clearAllFilters}
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        </InfiniteScroll>
      )}
    </MainPageWrapper>
  );
}
